// @flow

import * as React from 'react';
import { Flex, Box, useSystem } from 'react-system';
import LinearProgress from '@material-ui/core/LinearProgress';
import * as icons from '../../icons';
import { useTheme } from '../../theme';
import { Link } from '../link';
import { A } from '../A';

type Props = {|
  currentStep: number,
  maxStep: number,
  steps: Array<{|
    label: string,
    index: number,
    href: {| +pathname: string, +query?: { [string]: string, ... } |},
  |}>,
|};

export const ChatSteps = ({
  steps,
  currentStep,
  maxStep,
}: Props): React.Node => {
  const { media } = useSystem();
  const { text, colors } = useTheme();

  const separator = (
    <icons.KeyboardArrowRight size={20} css={{ margin: '0 4px' }} />
  );

  // Breadcrumbs
  const breadcrumbs = steps
    .map(({ label, href }, i) => {
      const index = i + 1;
      let item = label;
      if (index === currentStep) {
        item = <strong>{label}</strong>;
      } else if (index <= maxStep) {
        item = (
          <Link href={href}>
            <A color={colors.primary.main} underline={false}>
              {label}
            </A>
          </Link>
        );
      }
      //eslint-disable-next-line
      return <Box css={text.body2}>{item}</Box>;
    })
    .reduce(
      (p, c, i, a) => (i < a.length - 1 ? [...p, c, separator] : [...p, c]),
      [],
    )
    .map((item, i) => (
      <li key={`item-${i}`} css={{ display: 'inline-flex' }}>
        {item}
      </li>
    ));

  // Mobile Pagination
  const left =
    currentStep === 1 ? null : (
      <Flex alignItems="center" css={text.body2}>
        <icons.KeyboardArrowLeft size={18} fill={colors.primary.main} />
        <Link href={steps[currentStep - 2].href}>
          <A color={colors.primary.main} underline={false}>
            {steps[currentStep - 2].label}
          </A>
        </Link>
      </Flex>
    );

  const right =
    currentStep === steps.length ? null : currentStep < maxStep ? (
      <Flex alignItems="center" css={text.body2}>
        <Link href={steps[currentStep].href}>
          <A color={colors.primary.main} underline={false}>
            {steps[currentStep].label}
          </A>
        </Link>
        <icons.KeyboardArrowRight size={18} fill={colors.primary.main} />
      </Flex>
    ) : (
      <Flex alignItems="center" css={text.body2}>
        {steps[currentStep].label}
        <icons.KeyboardArrowRight size={18} />
      </Flex>
    );

  return (
    <Box>
      <LinearProgress
        variant="determinate"
        value={(currentStep / steps.length) * 100}
      />
      <Flex
        alignItems="center"
        py={2}
        css={media({ display: ['none', 'flex'] })}
      >
        {breadcrumbs}
      </Flex>
      <Flex
        alignItems="center"
        py={2}
        css={media({ display: ['flex', 'none'] })}
      >
        {left}
        <div css={{ flex: '1' }} />
        {right}
      </Flex>
    </Box>
  );
};
